import { Check, Languages, LogOut, Monitor, Moon, Search, Sun, UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { useUpdateMe } from "@/api/queries";

import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { languages, type Language } from "@/i18n";

import { useAuth, useCurrentUser } from "../auth";
import { useTheme, type Theme } from "../theme";
import { NotificationsMenu } from "./NotificationsMenu";

export function Topbar() {
  const { t } = useTranslation();

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b bg-surface/80 px-4 backdrop-blur md:px-6">
      <div className="relative max-w-md flex-1">
        <Search
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted"
          aria-hidden="true"
        />
        <input
          type="search"
          disabled
          placeholder={t("topbar.search")}
          aria-label={t("topbar.search")}
          className="h-9 w-full rounded-lg border-0 bg-surface-muted pr-3 pl-9 placeholder:text-muted disabled:cursor-not-allowed"
        />
      </div>

      <div className="ml-auto flex items-center gap-1">
        <NotificationsMenu />
        <LanguageMenu />
        <ThemeMenu />
        <UserMenu />
      </div>
    </header>
  );
}

function LanguageMenu() {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const updateMe = useUpdateMe();
  const current = i18n.resolvedLanguage ?? "ru";

  // The language is saved in the profile: emails use it too.
  const choose = (lng: Language) => {
    void i18n.changeLanguage(lng);
    if (auth.status === "authenticated" && auth.me.user.locale !== lng) {
      updateMe.mutate({ locale: lng }, { onSuccess: (me) => auth.setMe(me) });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" aria-label={t("topbar.language")} className="h-9 px-2.5">
          <Languages />
          <span className="uppercase">{current}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>{t("topbar.language")}</DropdownMenuLabel>
        {languages.map((lng) => (
          <DropdownMenuItem key={lng} onSelect={() => choose(lng)}>
            <span className="flex-1">{t(`languages.${lng}`)}</span>
            {lng === current && <Check className="text-primary" aria-hidden="true" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const themeIcons = { light: Sun, dark: Moon, system: Monitor } as const;

function ThemeMenu() {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const Icon = themeIcons[theme];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t("topbar.theme")}>
          <Icon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>{t("topbar.theme")}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as Theme)}>
          <DropdownMenuRadioItem value="light">{t("topbar.themeLight")}</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">{t("topbar.themeDark")}</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system">{t("topbar.themeSystem")}</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu() {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = useCurrentUser();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t("user.menu")}
          className="ml-1 flex cursor-pointer items-center gap-2 rounded-lg p-1 pr-2 hover:bg-surface-muted"
        >
          <UserAvatar name={user.full_name} />
          <span className="hidden text-left leading-tight lg:block">
            <span className="block font-medium">{user.full_name}</span>
            <span className="block text-xs text-muted">{t(`roles.${user.role}`)}</span>
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56">
        <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings/profile">
            <UserRound /> {t("topbar.profile")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void auth.logout()}>
          <LogOut /> {t("topbar.signOut")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
